import { IconButton, Stack, TextField, Tooltip } from '@mui/material';

import type { Control, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { z } from 'zod';

import { Folder } from '@mui/icons-material';

import { useState } from 'react';

import { LinkInputWithSearcher } from '@/components/input/LinkInputWithSearcher';

import { graphql, type DocumentType } from '@/gql';
import { nonFalsey } from '@/utils/nonFalsey';
import { useGetFileUrl } from '@/modules/files/hooks/useGetFileUrl';

import { FilePicker } from '../../file-picker';

const URL_FILE_FORM_PARENT_FRAGMENT = graphql(`
  fragment urlFileFormParentFields on File {
    _id
    filename
  }
`);

const URL_FILE_FORM_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment urlFileFormFilesForOwnerFields on FilesForOwner {
    owner {
      ... on Group {
        _id
        name
        machineName
      }
      ... on Service {
        _id
        name
        machineName
      }
    }
  }
`);

export const UrlFileFormSchema = z.object({
  filename: z.string(),
  url: z.string(),
});

export type UrlFileFormData = z.infer<typeof UrlFileFormSchema>;

interface Props {
  parent: DocumentType<typeof URL_FILE_FORM_PARENT_FRAGMENT> | null;
  filesForOwner: DocumentType<typeof URL_FILE_FORM_FILES_FOR_OWNER_FRAGMENT>;
  control: Control<UrlFileFormData>;
  setValue: UseFormSetValue<UrlFileFormData>;
}

export const UrlFileForm = ({
  parent,
  filesForOwner,
  control,
  setValue,
}: Props) => {
  const { owner: rootOwner } = filesForOwner;

  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [filePickerParentId, setFilePickerParentId] = useState<
    string | null | undefined
  >(parent?._id);

  const getFileUrl = useGetFileUrl({
    type: rootOwner.__typename,
    machineName: rootOwner.machineName ?? '',
  });

  const ownerIds = [rootOwner._id].filter(nonFalsey);

  return (
    <>
      <Stack spacing={1} sx={{ marginTop: 1 }}>
        <Controller
          name="filename"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              autoFocus
              margin="dense"
              id="weblinkname"
              label="Link Name"
              type="text"
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputRef={field.ref}
              value={field.value}
              onBlur={field.onBlur}
              onChange={field.onChange}
            />
          )}
        />
        <Stack alignItems="center" direction="row">
          <Controller
            name="url"
            control={control}
            render={({ field, fieldState }) => (
              <LinkInputWithSearcher
                ownerIds={ownerIds}
                value={field.value}
                error={Boolean(fieldState.error)}
                onChange={(file) => {
                  setValue('filename', file.filename ?? '');
                  setValue('url', getFileUrl(file));
                }}
                onInputChange={field.onChange}
              />
            )}
          />
          <Tooltip title="Browse for a file or folder">
            <IconButton onClick={() => setFilePickerOpen(true)} size="large">
              <Folder />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <FilePicker
        preventRootSelection
        selectionMode="both"
        open={filePickerOpen}
        onClose={() => setFilePickerOpen(false)}
        rootOwner={{
          _id: rootOwner._id ?? '',
          type: rootOwner.__typename,
        }}
        title="Browse for a file"
        parentActionName="Select this"
        fileActionName="Select"
        parentId={filePickerParentId}
        onChangeParent={(newFilePickerParentId) =>
          setFilePickerParentId(newFilePickerParentId)
        }
        onSelect={(pickerfileId, _ownerId, pickerFile) => {
          setFilePickerOpen(false);

          if (!(pickerfileId && pickerFile)) {
            return;
          }

          const newUrl = getFileUrl(pickerFile);
          if (newUrl) {
            setValue('url', newUrl);
          }

          if (pickerFile?.filename) {
            setValue('filename', pickerFile.filename);
          }
        }}
      />
    </>
  );
};
